import React from 'react';
import classNames from 'classnames';
import ProgressIndicator from '../../../../../components/Schedule/progress-indicator';
import styles from '../grid.module.scss';

export const formatHours = (value: number): number => Math.round(value * 10) / 10;

export type TimeBankLabel = 'putInto' | 'takenFrom';

type TableTagStyle = React.CSSProperties & {
    '--table-tag-color': string;
};

type HoursValueProps = {
    value: number;
    total?: number;
    type?: 'approve' | 'billable' | 'absence';
    hideZero?: boolean;
    mutedZero?: boolean;
    regularWeight?: boolean;
};

const createTableTagStyle = (color: string): TableTagStyle => ({
    '--table-tag-color': color,
});

const timeBankPutIntoTagStyle = createTableTagStyle('var(--text-success, #59ca59)');
const timeBankTakenFromTagStyle = createTableTagStyle('var(--text-danger, #ff6c6c)');

export const getTimeBankLabel = (timeBankHours?: number): TimeBankLabel | undefined => {
    if (!timeBankHours) {
        return undefined;
    }

    return timeBankHours > 0 ? 'putInto' : 'takenFrom';
};

const getTimeBankTagStyle = (label: TimeBankLabel): TableTagStyle =>
    label === 'putInto' ? timeBankPutIntoTagStyle : timeBankTakenFromTagStyle;

export const getTimeBankCellTitle = (label: TimeBankLabel): string =>
    label === 'putInto' ? 'Put into time bank' : 'Taken from time bank';

export const TimeBankCellStripe: React.FC<{ label: TimeBankLabel; className?: string }> = ({label, className}) => (
    <span
        className={classNames(styles.timeBankCellStripe, className)}
        style={getTimeBankTagStyle(label)}
    />
);

export const HoursValue: React.FC<HoursValueProps> = ({
    value,
    total = 0,
    type,
    hideZero,
    mutedZero,
    regularWeight
}) => {
    const displayValue = value === 0 && hideZero ? '' : formatHours(value);
    const progressValue = total > 0 ? (value / total) * 100 : 0;
    const progressType = type === 'approve' || type === 'billable' ? type : undefined;
    const showProgress = progressType !== undefined && value >= 0 && Number.isFinite(progressValue);
    const isZero = value === 0;
    const shouldMuteZero = mutedZero !== false && isZero;
    const valueClasses = classNames(styles.valueText, {
        [styles.valueApprove]: type === 'approve',
        [styles.valueBillable]: type === 'billable',
        [styles.valueAbsence]: type === 'absence',
        [styles.valueTextRegular]: regularWeight,
    });

    return (
        <div
            className={classNames(styles.hoursValue, {
                [styles.hoursValueMuted]: shouldMuteZero
            })}
        >
            <span className={valueClasses}>{displayValue}</span>
            {showProgress && (
                <span className={styles.progress}>
                    <ProgressIndicator type={progressType} value={progressValue} showWhenZero={true}/>
                </span>
            )}
        </div>
    );
};

type ActionIconButtonProps = {
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    title?: string;
    variant?: 'approve' | 'billable' | 'default';
};

export const ActionIconButton: React.FC<ActionIconButtonProps> = ({
    icon,
    label,
    active,
    disabled,
    onClick,
    title,
    variant = 'default'
}) => (
    <button
        type='button'
        className={classNames(styles.iconButton, {
            [styles.iconButtonActive]: active,
            [styles.iconButtonApprove]: variant === 'approve',
            [styles.iconButtonBillable]: variant === 'billable'
        })}
        aria-label={label}
        title={title ?? label}
        disabled={disabled}
        onClick={onClick}
    >
        {icon}
    </button>
);
